<template>
  <div class="operations-dashboard">
    <!-- Emergency Safe Mode Display -->
    <div v-if="safeMode" class="pa-4">
      <v-alert type="warning" class="mb-4">
        🛠️ Dashboard is running in safe mode due to an error
      </v-alert>
      <v-card class="pa-4">
        <h2>Operations Dashboard</h2>
        <p>Loading: {{ loading }}</p>
        <p>Total Users: {{ totalUsers }}</p>
        <p>Brand Tree Length: {{ brandWorkspacesTree.length }}</p>
        <v-btn @click="safeMode = false; loadDashboardData()" color="primary">
          Try Full Mode
        </v-btn>
      </v-card>
    </div>

    <!-- Full Dashboard -->
    <div v-else>


    <!-- User Filter Controls -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="text-h6 primary white--text py-3">
        <v-icon left color="white">mdi-filter</v-icon>
        User Filters
      </v-card-title>
      <v-card-text class="pa-4">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="emailFilter"
              label="Email Addresses (comma-separated)"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-email"
              placeholder="user1@example.com, user2@example.com"
              hint="Enter email addresses separated by commas"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="userIdFilter"
              label="User IDs (comma-separated)"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-account"
              placeholder="userId1, userId2, userId3"
              hint="Enter user IDs separated by commas"
              persistent-hint
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row class="mt-2">
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-btn 
                @click="loadDashboardData" 
                :loading="loading" 
                color="primary"
                :disabled="!hasValidFilters"
              >
                <v-icon left>mdi-refresh</v-icon>
                Load Data
              </v-btn>
              <v-btn 
                @click="clearUserFilters" 
                color="grey" 
                text
                class="ml-2"
              >
                <v-icon left>mdi-filter-remove</v-icon>
                Clear Filters
              </v-btn>
              <v-spacer></v-spacer>
              <v-chip 
                v-if="hasValidFilters" 
                small 
                color="info" 
                dark
              >
                {{ getFilterDescription }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Compact Summary Cards -->
    <v-row class="mb-4">
      <v-col cols="6" md="3">
        <v-card outlined class="text-center pa-3">
          <div class="text-h4 primary--text font-weight-bold">{{ totalUsers }}</div>
          <div class="text-caption">Total Users</div>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="text-center pa-3">
          <div class="text-h4 success--text font-weight-bold">{{ boostPlanUsers }}</div>
          <div class="text-caption">Users w/ Workspaces</div>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="text-center pa-3">
          <div class="text-h4 warning--text font-weight-bold">{{ totalParentWorkspaces }}</div>
          <div class="text-caption">Parent Workspaces</div>
        </v-card>
      </v-col>
      <v-col cols="6" md="3">
        <v-card outlined class="text-center pa-3">
          <div class="text-h4 info--text font-weight-bold">{{ locations.length }}</div>
          <div class="text-caption">Total Locations</div>
        </v-card>
      </v-col>
    </v-row>



    <!-- Brand Workspaces Table -->
    <v-card class="mb-4" elevation="2">
      <v-card-title class="text-h6 primary white--text py-3">
        <v-icon left color="white">mdi-domain</v-icon>
        Brand Workspaces & Store Locations

        <v-chip small class="ml-2" color="purple" dark v-if="boostPlanUsers <= 2">
          <v-icon left small>mdi-account-check</v-icon>
          Test Users Only
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn 
          @click="loadDashboardData" 
          :loading="loading" 
          color="white" 
          text
          small
          class="mr-2"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>

      </v-card-title>
      <v-card-text class="pa-4">
        <!-- Search and Filter Controls -->
        <div v-if="!loading && brandWorkspacesTree.length > 0" class="mb-4">
          <v-row>
            <!-- Search Bar -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchQuery"
                label="Search brands by name"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-magnify"
                placeholder="Type brand name to search..."
              ></v-text-field>
            </v-col>
            
            <!-- Country Filter -->
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedCountry"
                :items="countryOptions"
                item-text="label"
                item-value="code"
                label="Filter by Country"
                outlined
                dense
                clearable
                prepend-inner-icon="mdi-earth"
              >
                <template v-slot:selection="{ item }">
                  <v-chip color="primary" small dark>
                    {{ item.label }} ({{ item.count }})
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <div class="d-flex justify-space-between align-center w-100">
                    <span>{{ item.label }}</span>
                    <v-chip small color="info" dark>{{ item.count }}</v-chip>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>
        
        <!-- Data Table -->
        <div v-if="!loading">
          <v-data-table
            :headers="brandTableHeaders"
            :items="filteredBrandTree"
            class="elevation-1"
            :items-per-page="25"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            item-key="id"
            show-expand
            :expanded.sync="expanded"
            @item-expanded="onBrandExpanded"
          >
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <v-icon small class="mr-2" :color="item.isBrand ? 'primary' : (item.gmbConnected === 'Yes' ? 'success' : 'grey')">
                  {{ item.isBrand ? 'mdi-domain' : (item.type === 'Store' ? 'mdi-store' : 'mdi-map-marker') }}
                </v-icon>
                <div>
                  <div :class="item.isBrand ? 'font-weight-bold text-h6' : 'font-weight-medium'">{{ item.name }}</div>
                  <div class="text-caption grey--text" v-if="item.isBrand && item.workspaceName">
                    Workspace: {{ item.workspaceName }}
                  </div>
                  <div class="text-caption grey--text" v-if="item.isBrand">{{ item.uid }}</div>
                </div>
              </div>
            </template>
            
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <v-simple-table dense>
                  <thead>
                    <tr class="store-header-row">
                      <th class="pl-8 text-left">Store/Location Name</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Locations</th>
                      <th class="text-left">GMB Connected</th>
                      <th class="text-left">Recent Post</th>
                      <th class="text-left">Scheduled Posts</th>
                      <th class="text-left">Scheduled Up To</th>
                      <th class="text-left">Footfall Analytics</th>
                      <th class="text-left">Review Dashboard</th>
                      <th class="text-left">Profile %</th>
                      <th class="text-left">Overall Rank</th>
                      <th class="text-left">Rank Refresh</th>
                      <th class="text-left">Review Insights</th>
                      <th class="text-left">Keyword Insights</th>
                      <th class="text-left">Competitors</th>
                      <th class="text-left">Apify Fetch</th>
                      <th class="text-left">Footfall Insights</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="store in item.children" :key="store.id" class="store-row">
                      <td class="pl-8">
                        <div class="d-flex align-center">
                          <v-icon small class="mr-2" :color="store.gmbConnected === 'Yes' ? 'success' : 'grey'">
                            {{ store.type === 'Store' ? 'mdi-store' : 'mdi-map-marker' }}
                          </v-icon>
                          <span>{{ store.name.replace('└── ', '') }}</span>
                        </div>
                      </td>
                      <td>{{ store.type }}</td>
                      <td>{{ store.locationCount }}</td>
                      <td>{{ store.gmbConnected }}</td>
                      <td class="grey--text">-</td> <!-- Recent Post - N/A for stores -->
                      <td class="grey--text">-</td> <!-- Scheduled Posts - N/A for stores -->
                      <td class="grey--text">-</td> <!-- Scheduled Up To - N/A for stores -->
                      <td class="grey--text">-</td> <!-- Footfall Analytics - N/A for stores -->
                      <td class="grey--text">-</td> <!-- Review Dashboard - N/A for stores -->
                      <td>
                        <v-progress-circular
                          :value="store.profileCompletion"
                          :color="store.profileCompletion >= 80 ? 'success' : store.profileCompletion >= 60 ? 'warning' : 'error'"
                          size="24"
                          width="2"
                        >
                          <span class="text-caption" style="font-size: 10px;">{{ store.profileCompletion }}%</span>
                        </v-progress-circular>
                      </td>
                      <td class="grey--text">-</td> <!-- Overall Rank - N/A for stores -->
                      <td>{{ formatDate(store.rankRefresh) || 'Never' }}</td>
                      <td>{{ formatDate(store.reviewInsights) || 'Never' }}</td>
                      <td>{{ formatDate(store.keywordInsights) || 'Never' }}</td>
                      <td>{{ formatDate(store.competitors) || 'Never' }}</td>
                      <td>{{ formatDate(store.apifyFetch) || 'Never' }}</td>
                      <td>{{ formatDate(store.footfallInsights) || 'Never' }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </td>
            </template>
            <template v-slot:item.type="{ item }">
              <v-chip x-small :color="item.isBrand ? 'primary' : (item.type === 'Store' ? 'info' : 'secondary')" dark>
                {{ item.type }}
              </v-chip>
            </template>
            <template v-slot:item.locationCount="{ item }">
              <v-chip small :color="item.locationCount > 0 ? 'success' : 'grey'" dark>
                {{ item.locationCount }}
              </v-chip>
            </template>
            <template v-slot:item.gmbConnected="{ item }">
              <span v-if="item.isBrand" class="text-caption">{{ item.gmbConnected }}</span>
              <v-chip v-else x-small :color="item.gmbConnected === 'Yes' ? 'success' : 'error'" dark>
                {{ item.gmbConnected }}
              </v-chip>
            </template>
            <template v-slot:item.recentPost="{ item }">
              <div v-if="item.isBrand && item.recentPost">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div class="text-caption" v-bind="attrs" v-on="on">
                      {{ item.recentPostDaysAgo ? `${item.recentPostDaysAgo} day${item.recentPostDaysAgo !== 1 ? 's' : ''} ago` : formatDate(item.recentPost) }}
                    </div>
                  </template>
                  <span>{{ formatDate(item.recentPost) }}</span>
                </v-tooltip>
              </div>
              <span v-else-if="item.isBrand" class="grey--text">No posts</span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.scheduledPosts="{ item }">
              <span v-if="item.isBrand">
                <v-tooltip bottom max-width="600">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip small :color="item.scheduledPosts > 0 ? 'success' : 'grey'" dark v-bind="attrs" v-on="on">
                  {{ item.scheduledPosts }}
                </v-chip>
                  </template>
                  <v-card v-if="item.scheduledPostsDetails && item.scheduledPostsDetails.length > 0" class="pa-2" color="white" elevation="3" max-width="400">
                    <div class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center">
                      <v-icon small color="primary" class="mr-1">mdi-timeline-clock</v-icon>
                      Scheduled Timeline ({{ item.scheduledPosts }})
                    </div>
                    
                    <!-- Compact Timeline View -->
                    <div class="timeline-container" style="max-height: 300px; overflow-y: auto;">
                      <div v-for="(dateGroup, index) in item.scheduledPostsDetails" v-bind:key="index" class="timeline-item d-flex mb-2">
                        <!-- Timeline Line -->
                        <div class="timeline-line d-flex flex-column align-center mr-3">
                          <v-icon small color="primary">mdi-circle</v-icon>
                          <div v-if="index < item.scheduledPostsDetails.length - 1" class="timeline-connector"></div>
                        </div>
                        
                        <!-- Content -->
                        <div class="timeline-content flex-grow-1">
                          <div class="d-flex align-center mb-1">
                            <span class="font-weight-medium text-subtitle-2 primary--text">{{ dateGroup.date }}</span>
                            <v-chip x-small color="primary" class="ml-2">{{ dateGroup.count }}</v-chip>
                          </div>
                          
                          <!-- Posts for this date -->
                          <div v-for="(post, postIndex) in dateGroup.posts" v-bind:key="postIndex" class="post-item d-flex align-center mb-1">
                            <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                            <span class="text-caption mr-2">{{ formatTime(post.scheduledTime) }}</span>
                            
                            <!-- Platform icons inline -->
                            <div class="d-flex">
                              <v-tooltip 
                                v-for="platform in (post.platforms || [])" 
                                :key="platform" 
                                top
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon 
                                    x-small 
                                    :color="getPlatformColor(platform)" 
                                    class="mr-1" 
                                    v-bind="attrs" 
                                    v-on="on"
                                  >
                                    {{ getPlatformIcon(platform) }}
                                  </v-icon>
                                </template>
                                <span>{{ getPlatformName(platform) }}</span>
                              </v-tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-card>
                  <v-card v-else class="pa-3" color="white" elevation="3">
                    <span class="grey--text">No scheduled posts</span>
                  </v-card>
                </v-tooltip>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.scheduledUpToDate="{ item }">
              <span v-if="item.isBrand">
                <div v-if="item.scheduledUpToDate" class="text-caption">
                  {{ formatDate(item.scheduledUpToDate) }}
                </div>
                <span v-else class="grey--text">No schedule</span>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.footfallAnalyticsDefault="{ item }">
              <span v-if="item.isBrand">
                <v-tooltip bottom v-if="item.footfallAnalyticsDefault">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip 
                      x-small 
                      :color="item.footfallAnalyticsDefault.isUpToDate ? 'success' : 'error'" 
                      dark 
                      v-bind="attrs" 
                      v-on="on"
                    >
                      {{ formatFootfallDate(item.footfallAnalyticsDefault.date) }}
                    </v-chip>
                  </template>
                  <div>
                    <div class="font-weight-bold">Footfall Analytics (Default)</div>
                    <div class="text-caption">Date: {{ formatFootfallDate(item.footfallAnalyticsDefault.date) }}</div>
                    <div class="text-caption">Generated: {{ formatDateFull(item.footfallAnalyticsDefault.generated_at) }}</div>
                    <div class="text-caption">Status: {{ item.footfallAnalyticsDefault.isUpToDate ? 'Up to date' : 'Needs refresh (Monday passed)' }}</div>
                  </div>
                </v-tooltip>
                <span v-else class="grey--text">No data</span>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.reviewDashboard="{ item }">
              <span v-if="item.isBrand">
                <v-tooltip bottom v-if="item.reviewDashboard">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip 
                      x-small 
                      :color="item.reviewDashboard.isUpToDate ? 'success' : 'error'" 
                      dark 
                      v-bind="attrs" 
                      v-on="on"
                    >
                      {{ formatDate(item.reviewDashboard.updated_at) }}
                    </v-chip>
                  </template>
                  <div>
                    <div class="font-weight-bold">Review Dashboard</div>
                    <div class="text-caption">Updated: {{ formatDate(item.reviewDashboard.updated_at) }}</div>
                    <div class="text-caption">Document: {{ item.reviewDashboard.document_id }}</div>
                    <div class="text-caption">Status: {{ item.reviewDashboard.isUpToDate ? 'Up to date' : 'Needs refresh (Monday passed)' }}</div>
                  </div>
                </v-tooltip>
                <span v-else class="grey--text">No data</span>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.profileCompletion="{ item }">
              <v-progress-circular
                :value="item.profileCompletion"
                :color="item.profileCompletion >= 80 ? 'success' : item.profileCompletion >= 60 ? 'warning' : 'error'"
                size="30"
                width="3"
              >
                <span class="text-caption">{{ item.profileCompletion }}%</span>
              </v-progress-circular>
            </template>
            <template v-slot:item.overallRank="{ item }">
              <span v-if="item.isBrand">{{ item.overallRank || 'N/A' }}</span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.rankRefresh="{ item }">
              <div v-if="item.refreshStats.rankLastRefresh">
                <div class="text-caption">{{ formatDate(item.refreshStats.rankLastRefresh) }}</div>
              </div>
              <span v-else class="grey--text">Never</span>
            </template>
            <template v-slot:item.reviewInsights="{ item }">
              <div v-if="item.refreshStats.reviewInsightsLastRefresh">
                <div class="text-caption">{{ formatDate(item.refreshStats.reviewInsightsLastRefresh) }}</div>
              </div>
              <span v-else class="grey--text">Never</span>
            </template>
            <template v-slot:item.keywordInsightsSummary="{ item }">
              <span v-if="item.isBrand">
                <v-tooltip bottom v-if="item.keywordInsightsSummary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip 
                      x-small 
                      :color="item.keywordInsightsSummary.isUpToDate ? 'success' : 'warning'" 
                      dark 
                      v-bind="attrs" 
                      v-on="on"
                    >
                      {{ item.keywordInsightsSummary.monthYear }}
                    </v-chip>
                  </template>
                  <div>
                    <div class="font-weight-bold">Keyword Insights Summary</div>
                    <div class="text-caption">Month: {{ item.keywordInsightsSummary.monthYear }}</div>
                    <div class="text-caption">Last Updated: {{ formatDate(item.keywordInsightsSummary.lastUpdated) }}</div>
                    <div class="text-caption">Status: {{ item.keywordInsightsSummary.isUpToDate ? 'Up to date' : 'Needs refresh (Monday passed)' }}</div>
              </div>
                </v-tooltip>
                <span v-else class="grey--text">No data</span>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.competitors="{ item }">
              <div v-if="item.refreshStats.competitorsLastRefresh">
                <div class="text-caption">{{ formatDate(item.refreshStats.competitorsLastRefresh) }}</div>
              </div>
              <span v-else class="grey--text">Never</span>
            </template>
            <template v-slot:item.apifyFetch="{ item }">
              <div v-if="item.refreshStats.apifyFetchLastRefresh">
                <div class="text-caption">{{ formatDate(item.refreshStats.apifyFetchLastRefresh) }}</div>
              </div>
              <span v-else class="grey--text">Never</span>
            </template>
            <template v-slot:item.footfallInsights="{ item }">
              <div v-if="item.refreshStats.footfallInsightsLastRefresh">
                <div class="text-caption">{{ formatDate(item.refreshStats.footfallInsightsLastRefresh) }}</div>
              </div>
              <span v-else class="grey--text">Never</span>
            </template>
          </v-data-table>
          
          <!-- Debug info when table is empty -->
          <div v-if="filteredBrandTree.length === 0" class="text-center pa-8">
            <v-icon large color="grey">mdi-domain-off</v-icon>
            <div class="text-h6 mt-2">
              <span v-if="searchQuery || selectedCountry">No Matching Brand Workspaces</span>
              <span v-else>No Brand Workspaces Found</span>
            </div>
            <div class="text-body-2 grey--text mt-2">
              <span v-if="searchQuery || selectedCountry">
                Showing 0 of {{ brandWorkspacesTree.length }} total brands
                <span v-if="searchQuery">(search: "{{ searchQuery }}")</span>
                <span v-if="selectedCountry">(country: {{ getCountryName(selectedCountry) }})</span>
              </span>
              <span v-else>
                {{ brandWorkspacesTree.length }} total brands loaded
              </span>
            </div>
            <div class="text-caption grey--text mt-1" v-if="searchQuery || selectedCountry">
              <v-btn @click="clearFilters" color="primary" text small>
                <v-icon left small>mdi-filter-remove</v-icon>
                Clear Filters
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>



    <!-- Loading Overlay -->
    <v-overlay :value="loading" absolute>
      <div class="text-center">
        <v-progress-circular indeterminate size="64" color="white"></v-progress-circular>
        <div class="mt-4 text-h6 white--text">Loading Dashboard...</div>
        
        <!-- Progress Bar for User Processing -->
        <div v-if="userProcessingProgress.total > 0" class="mt-6 mx-auto" style="max-width: 600px;">
          <div class="text-subtitle-1 mb-2 white--text">
            Processing Users: {{ userProcessingProgress.current }} / {{ userProcessingProgress.total }}
          </div>
          <v-progress-linear
            :value="(userProcessingProgress.current / userProcessingProgress.total) * 100"
            height="25"
            color="primary"
            background-color="grey lighten-2"
            striped
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}%</strong>
            </template>
          </v-progress-linear>
          <div class="text-caption mt-2 grey--text text--lighten-2">
            Currently processing: {{ userProcessingProgress.currentUserEmail || 'Initializing...' }}
          </div>
        </div>
      </div>
    </v-overlay>
    
    </div> <!-- End v-else -->
  </div>
</template>

<script>
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  collectionGroup,
  orderBy,
  limit,
  startAfter
} from "firebase/firestore";
import { auth } from "@/utils/firebase.utils.js";
import { db } from "@/utils/firebase.utils.js";

export default {
  name: "OperationsDashboard",
  errorCaptured(err, vm, info) {
    console.error('Vue Error Captured in OperationsDashboard:', err);
    console.error('Component info:', info);
    console.error('Vue instance:', vm);
    return false; // Allow error to propagate
  },
  created() {
    console.log('🔧 OperationsDashboard created');
    
    // Add global error handler
    window.addEventListener('error', (event) => {
      console.error('🚨 Global Error Caught:', event.error);
      console.error('🚨 Error message:', event.message);
      console.error('🚨 Error filename:', event.filename);
      console.error('🚨 Error line:', event.lineno);
      console.error('🚨 Error column:', event.colno);
    });
    
    window.addEventListener('unhandledrejection', (event) => {
      console.error('🚨 Unhandled Promise Rejection:', event.reason);
      console.error('🚨 Promise:', event.promise);
    });
  },
  data() {
    return {
      loading: true,
      safeMode: false, // Emergency fallback mode
      totalUsers: 0,
      boostPlanUsers: 0,
      totalParentWorkspaces: 0,
      usersByCountry: {},
      selectedCountry: null,
      countryOptions: [],
      searchQuery: '',
      locations: [],
      parentWorkspaces: [],
      brandWorkspacesTree: [],
      expanded: [],
      sortBy: [],
      sortDesc: [],
      userProcessingProgress: {
        current: 0,
        total: 0,
        currentUserEmail: ''
      },
      planDistribution: {},
      userLimit: "all",
      // User filter controls
      emailFilter: 'kedar.deshmukh@zoomlocal.in, adarsh.dinni@zoomlocal.in',
      userIdFilter: '',
      brandTableHeaders: [
        { text: 'Brand / Store', value: 'name' },
        { text: 'Type', value: 'type' },
        { text: 'Locations', value: 'locationCount' },
        { text: 'GMB Connected', value: 'gmbConnected' },
        { text: 'Recent Post', value: 'recentPost', sortable: true },
        { text: 'Scheduled Posts', value: 'scheduledPosts', sortable: true },
        { text: 'Scheduled Up To', value: 'scheduledUpToDate', sortable: true },
        { text: 'Footfall Analytics', value: 'footfallAnalyticsDefault', sortable: true },
        { text: 'Review Dashboard', value: 'reviewDashboard', sortable: true },
        { text: 'Profile %', value: 'profileCompletion', sortable: true },
        { text: 'Overall Rank', value: 'overallRank', sortable: true },
        { text: 'Rank Refresh', value: 'rankRefresh', sortable: true },
        { text: 'Review Insights', value: 'reviewInsights', sortable: true },
        { text: 'Keyword Insights', value: 'keywordInsightsSummary', sortable: true },
        { text: 'Competitors', value: 'competitors', sortable: true },
        { text: 'Apify Fetch', value: 'apifyFetch', sortable: true },
        { text: 'Footfall Insights', value: 'footfallInsights', sortable: true },
      ],

    };
  },
  computed: {
    filteredBrandTree() {
      try {
        console.log('📊 Computing filteredBrandTree, selectedCountry:', this.selectedCountry, 'searchQuery:', this.searchQuery);
        console.log('📊 brandWorkspacesTree length:', this.brandWorkspacesTree?.length || 0);
        
        let filtered = this.brandWorkspacesTree || [];
        
        // Apply country filter
        if (this.selectedCountry) {
          filtered = filtered.filter(brand => 
            brand && brand.country === this.selectedCountry
          );
        }
        
        // Apply search filter
        if (this.searchQuery && this.searchQuery.trim()) {
          const searchTerm = this.searchQuery.trim().toLowerCase();
          filtered = filtered.filter(brand => {
            if (!brand) return false;
            
            // Search in brand name, workspace name, and brand name
            const brandName = (brand.name || '').toLowerCase();
            const workspaceName = (brand.workspaceName || '').toLowerCase();
            
            return brandName.includes(searchTerm) || workspaceName.includes(searchTerm);
          });
        }
        
        console.log('📊 Filtered results:', filtered.length, '(country:', this.selectedCountry, 'search:', this.searchQuery, ')');
        return filtered;
      } catch (error) {
        console.error('❌ Error in filteredBrandTree computed:', error);
        return [];
      }
    },

    hasValidFilters() {
      const hasEmails = this.emailFilter && this.emailFilter.trim();
      const hasUserIds = this.userIdFilter && this.userIdFilter.trim();
      return hasEmails || hasUserIds;
    },

    getFilterDescription() {
      const emailCount = this.emailFilter ? this.emailFilter.split(',').filter(e => e.trim()).length : 0;
      const userIdCount = this.userIdFilter ? this.userIdFilter.split(',').filter(u => u.trim()).length : 0;
      
      const parts = [];
      if (emailCount > 0) parts.push(`${emailCount} email${emailCount > 1 ? 's' : ''}`);
      if (userIdCount > 0) parts.push(`${userIdCount} user ID${userIdCount > 1 ? 's' : ''}`);
      
      return parts.join(' + ');
    }
  },
  watch: {
    userLimit() {
      // Auto-refresh when user limit changes
      this.loadDashboardData();
    }
  },
  async mounted() {
    try {
      console.log('🚀 Operations Dashboard mounted');
      await this.checkAdminStatus();
      console.log('✅ Admin status checked');
      
      // Don't auto-load data, wait for user to click Load Data button
      this.loading = false;
      console.log('✅ Dashboard ready - waiting for user filters');
    } catch (error) {
      console.error('❌ Error in mounted lifecycle:', error);
      console.error('❌ Switching to safe mode');
      
      this.loading = false;
      this.safeMode = true; // Enable safe mode
      
      // Set some default data to prevent template errors
      this.totalUsers = 0;
      this.boostPlanUsers = 0;
      this.totalParentWorkspaces = 0;
      this.locations = [];
      this.brandWorkspacesTree = [];
      this.countryOptions = [];
      
      // Show user-friendly error
      if (this.$snackbar) {
        this.$snackbar.show('Dashboard error - running in safe mode. Check console for details.');
      }
    }
  },
  methods: {
    async checkAdminStatus() {
      try {
        const currentUID = auth.currentUser?.uid;
        if (!currentUID) {
          this.$snackbar?.show("Please login to access this page");
          this.$router.push("/login");
          return;
        }

        const userRef = doc(db, "users", currentUID);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (!("super_admin" in userData && userData.super_admin)) {
            this.$snackbar?.show("Direct access is prohibited");
            this.$router.push("/calendar");
            return;
          }
        } else {
          this.$snackbar?.show("User not found");
          this.$router.push("/login");
          return;
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
        this.$snackbar?.show("Direct access is prohibited");
        this.$router.push("/calendar");
      }
    },

    async loadDashboardData() {
      console.log('🔄 Starting loadDashboardData');
      this.loading = true;
      
      try {
        console.log('📊 Loading user statistics...');
        await this.loadUserStatistics();
        console.log('✅ User statistics loaded successfully');
      } catch (error) {
        console.error("❌ Error loading dashboard data:", error);
        console.error("❌ Error stack:", error.stack);
        this.$snackbar?.show("Error loading dashboard data: " + error.message);
        
        // Set safe defaults to prevent template errors
        this.totalUsers = 0;
        this.boostPlanUsers = 0;
        this.totalParentWorkspaces = 0;
        this.locations = [];
        this.brandWorkspacesTree = [];
        this.countryOptions = [];
      } finally {
        this.loading = false;
        console.log('🏁 loadDashboardData completed');
      }
    },

    async loadUserStatistics() {
      
      try {
        // Parse user filters
        const emailList = this.emailFilter 
          ? this.emailFilter.split(',').map(email => email.trim()).filter(email => email)
          : [];
        const userIdList = this.userIdFilter 
          ? this.userIdFilter.split(',').map(id => id.trim()).filter(id => id)
          : [];

        console.log('📧 Email filters:', emailList);
        console.log('👤 User ID filters:', userIdList);

        if (emailList.length === 0 && userIdList.length === 0) {
          console.error('❌ No valid filters provided');
          this.$snackbar?.show('Please provide either email addresses or user IDs to filter by');
          return;
        }

        const usersRef = collection(db, "users");
        let usersQuery;

        if (emailList.length > 0 && userIdList.length > 0) {
          // Both email and user ID filters provided - need to use multiple queries
          console.log('🔍 Querying users by both emails and user IDs...');
          
          // Query by emails
          const emailQuery = query(usersRef, where("email", "in", emailList));
          const emailSnapshot = await getDocs(emailQuery);
          
          // Query by user IDs (document IDs)
          const userIdDocs = [];
          for (const userId of userIdList) {
            try {
              const userDoc = await getDoc(doc(db, "users", userId));
              if (userDoc.exists()) {
                userIdDocs.push(userDoc);
              } else {
                console.warn(`User ID ${userId} not found`);
              }
            } catch (error) {
              console.warn(`Error fetching user ID ${userId}:`, error);
            }
          }
          
          // Combine results
          const allDocs = [...emailSnapshot.docs, ...userIdDocs];
          const uniqueDocs = allDocs.filter((doc, index, arr) => 
            arr.findIndex(d => d.id === doc.id) === index
          );
          
          const usersSnapshot = {
            docs: uniqueDocs,
            size: uniqueDocs.length,
            empty: uniqueDocs.length === 0
          };

          console.log(`📊 Combined query results: ${emailSnapshot.size} from emails + ${userIdDocs.length} from user IDs = ${usersSnapshot.size} unique users`);
          
        } else if (emailList.length > 0) {
          // Only email filters
          console.log('🔍 Querying users by emails only...');
          usersQuery = query(usersRef, where("email", "in", emailList));
          var usersSnapshot = await getDocs(usersQuery);
          
        } else {
          // Only user ID filters
          console.log('🔍 Querying users by user IDs only...');
          const userIdDocs = [];
          for (const userId of userIdList) {
            try {
              const userDoc = await getDoc(doc(db, "users", userId));
              if (userDoc.exists()) {
                userIdDocs.push(userDoc);
              } else {
                console.warn(`User ID ${userId} not found`);
              }
            } catch (error) {
              console.warn(`Error fetching user ID ${userId}:`, error);
            }
          }
          
          var usersSnapshot = {
            docs: userIdDocs,
            size: userIdDocs.length,
            empty: userIdDocs.length === 0
          };
        }
        
        if (usersSnapshot.empty) {
          console.warn('⚠️ No users found matching the provided filters');
          this.$snackbar?.show('No users found matching the provided email addresses or user IDs');
          this.totalUsers = 0;
          this.boostPlanUsers = 0;
          this.totalParentWorkspaces = 0;
          this.locations = [];
          this.brandWorkspacesTree = [];
          this.countryOptions = [];
          return;
        }
        
        this.totalUsers = usersSnapshot.size;
        console.log(`Total filtered users found: ${this.totalUsers}`);
        
        const countryCounts = {};
        const locationsList = [];
        const parentWorkspacesList = [];
        let boostUsersCount = 0;
        let boostUsersWithWorkspaces = 0;
        const planCounts = {};

        // Process all found users (should be max 2 specific test users)
        const userDocs = usersSnapshot.docs;
        console.log(`Processing ${userDocs.length} test users out of ${usersSnapshot.size} total found users`);
        
        // Initialize progress tracking
        this.userProcessingProgress.total = userDocs.length;
        this.userProcessingProgress.current = 0;

        for (const userDoc of userDocs) {
          const userData = userDoc.data();
          const uid = userDoc.id;
          
          // Update progress
          this.userProcessingProgress.current++;
          this.userProcessingProgress.currentUserEmail = userData.email || uid;
          
          // Allow UI to update with small delay to show progress
          await this.$nextTick();
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Count users by country
          const countryCode = userData.country_code || 'Unknown';
          countryCounts[countryCode] = (countryCounts[countryCode] || 0) + 1;

          // Count all users as they match our filter criteria
          boostUsersCount++;

          const plan = userData.fly_plan || 'undefined';
          planCounts[plan] = (planCounts[plan] || 0) + 1;
          
          // Log user details for debugging
          console.log(`Test user ${uid}:`, {
            email: userData.email,
            fly_plan: userData.fly_plan,
            country: countryCode
          });
          
          // Check if user has any workspaces
          try {
              const workspacesRef = collection(db, "users", uid, "fly_workspaces");
              const workspacesSnapshot = await getDocs(workspacesRef);
            
            console.log(`Test user ${uid} (${userData.email}) has ${workspacesSnapshot.size} workspaces`);
            
            // Only process users who have workspaces
            if (workspacesSnapshot.size > 0) {
              boostUsersWithWorkspaces++;
              console.log(`Processing test user with workspaces: ${userData.email || uid}`);
              
              const parentWorkspaces = new Map();
              const allWorkspaces = [];

              // First pass: Collect all workspaces and identify brand workspaces
              workspacesSnapshot.forEach(wsDoc => {
                const wsData = wsDoc.data();
                const wsId = wsDoc.id;
                
                // Store all workspaces for later scanning
                allWorkspaces.push({
                  id: wsId,
                  data: wsData,
                  uid: uid
                });
                
                // Log workspace structure for debugging
                console.log(`Workspace ${wsId} for user ${uid}:`, {
                  name: wsData.name,
                  brandName: wsData.brandName,
                  admin: wsData.admin,
                  gmbLocationId: wsData.gmbLocationId,
                  storeWorkspace: wsData.storeWorkspace,
                  allKeys: Object.keys(wsData)
                });
                
                // Identify brand workspaces: has brandName and no admin field (or admin is undefined/null/default)
                if (wsData.brandName && (!wsData.admin || wsData.admin === "default")) {
                  parentWorkspaces.set(wsId, {
                    id: wsId,
                    name: wsData.name || 'Unnamed Workspace',
                    brandName: wsData.brandName,
                    data: wsData,
                    createdAt: wsData.created_at,
                    country: countryCode,
                    uid: uid,
                    childCount: 0,
                    children: [] // Will store matched child workspaces
                  });
                  console.log(`✓ Brand workspace found: ${wsData.brandName} (${wsId})`);
                }
              });

              console.log(`🔍 Found ${parentWorkspaces.size} brand workspaces for user ${uid}`);
              console.log(`📋 Brand workspace IDs:`, Array.from(parentWorkspaces.keys()));

              // Count child workspaces but don't load them initially (for performance)
              parentWorkspaces.forEach((brandWorkspace, brandId) => {
                console.log(`🔍 Counting children for brand: ${brandWorkspace.brandName} (${brandId})`);
                
                let childCount = 0;
                allWorkspaces.forEach(workspace => {
                  const wsData = workspace.data;
                  // Check if this workspace has admin field matching the brand workspace ID
                  if (wsData.admin === brandId) {
                    childCount++;
                  }
                });
                
                brandWorkspace.childCount = childCount;
                brandWorkspace.children = []; // Will be loaded on-demand when expanded
                console.log(`🏁 Brand "${brandWorkspace.brandName}" has ${childCount} child workspaces (not loaded yet)`);
              });

              // Add parent workspaces to the list
              parentWorkspaces.forEach(parent => {
                parentWorkspacesList.push(parent);
              });

              console.log(`User ${uid} has ${parentWorkspaces.size} brand workspaces (child locations will be loaded on-demand)`);
              

            } else {
              // Test user has no workspaces
              console.log(`Test user ${userData.email || uid} has no workspaces - skipping`);
            }
          } catch (error) {
            console.warn(`Error loading workspaces for test user ${uid}:`, error);
          }
        }

        this.boostPlanUsers = boostUsersWithWorkspaces;
        this.usersByCountry = countryCounts;
        this.planDistribution = planCounts;
        
        // Create country options for dropdown based on locations
        const locationCountryCounts = {};
        locationsList.forEach(location => {
          const country = location.country || 'Unknown';
          locationCountryCounts[country] = (locationCountryCounts[country] || 0) + 1;
        });
        
        this.countryOptions = Object.entries(locationCountryCounts)
          .map(([code, count]) => ({
            code: code,
            label: this.getCountryName(code),
            count: count
          }))
          .sort((a, b) => b.count - a.count);



        // Set the locations data
        console.log(`=== FINAL RESULTS (Filtered Users Only) ===`);
        console.log(`Filtered users returned from query: ${usersSnapshot.size}`);
        console.log(`Filtered users processed: ${userDocs.length}`);
        console.log(`Plan distribution:`, planCounts);
        console.log(`Total filtered users: ${boostUsersCount}`);
        console.log(`Filtered users with workspaces: ${boostUsersWithWorkspaces}`);
        console.log(`Total brand workspaces found: ${parentWorkspacesList.length}`);
        console.log(`Total store locations found: ${locationsList.length}`);
        console.log('Sample brand workspaces:', parentWorkspacesList.slice(0, 3).map(p => ({
          brandName: p.brandName,
          workspaceName: p.name,
          childCount: p.childCount
        })));
        console.log('Sample locations:', locationsList.slice(0, 3));
        console.log(`Countries found:`, countryCounts);
        
        this.locations = locationsList;
        this.parentWorkspaces = parentWorkspacesList;
        this.totalParentWorkspaces = parentWorkspacesList.length;
        
        // Create brand workspace tree structure with metrics
        console.log('🔄 Creating brand tree with analytics...');
        this.brandWorkspacesTree = await this.createBrandTree(parentWorkspacesList, locationsList);
        console.log('✅ Brand tree with analytics completed');

        // Reset progress
        this.userProcessingProgress = {
          current: 0,
          total: 0,
          currentUserEmail: ''
        };



      } catch (error) {
        console.error("Error loading user statistics:", error);
        console.error("Full error details:", error);
        this.$snackbar?.show(`Error loading user statistics: ${error.message}`);
        
        // Reset progress on error
        this.userProcessingProgress = {
          current: 0,
          total: 0,
          currentUserEmail: ''
        };
        this.planDistribution = {};
        
        // Don't throw error so the page doesn't break completely
      }
    },

    async calculateLocationRefreshStats(uid, parentWid, locationId) {
      const stats = {
        rankLastRefresh: null,
        reviewInsightsLastRefresh: null,
        keywordInsightsLastRefresh: null,
        competitorsLastRefresh: null,
        apifyFetchLastRefresh: null,
        footfallInsightsLastRefresh: null
      };

      try {
        // Check rank tracker runs for this location
        const rankTrackerQuery = query(
          collection(db, "users", uid, "rank_tracker_runs"),
          where("workspace_id", "==", locationId),
          orderBy("created_at", "desc"),
          limit(1)
        );
        const rankTrackerSnapshot = await getDocs(rankTrackerQuery);
        if (!rankTrackerSnapshot.empty) {
          stats.rankLastRefresh = rankTrackerSnapshot.docs[0].data().created_at;
        }

        // Check review insights for this location
        const reviewInsightsQuery = query(
          collection(db, "users", uid, "fly_workspaces", locationId, "review_insights"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const reviewInsightsSnapshot = await getDocs(reviewInsightsQuery);
        if (!reviewInsightsSnapshot.empty) {
          stats.reviewInsightsLastRefresh = reviewInsightsSnapshot.docs[0].data().lastUpdated;
        }

        // Check keyword insights (search insights) for this location
        const keywordInsightsQuery = query(
          collection(db, "users", uid, "fly_workspaces", locationId, "search_insights"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const keywordInsightsSnapshot = await getDocs(keywordInsightsQuery);
        if (!keywordInsightsSnapshot.empty) {
          stats.keywordInsightsLastRefresh = keywordInsightsSnapshot.docs[0].data().lastUpdated;
        }

        // Check competitors data for this location
        const competitorsQuery = query(
          collection(db, "users", uid, "fly_workspaces", locationId, "competitors"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const competitorsSnapshot = await getDocs(competitorsQuery);
        if (!competitorsSnapshot.empty) {
          stats.competitorsLastRefresh = competitorsSnapshot.docs[0].data().lastUpdated;
        }

        // Check Apify fetch data for this location
        const apifyQuery = query(
          collection(db, "users", uid, "fly_workspaces", locationId, "apify_data"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const apifySnapshot = await getDocs(apifyQuery);
        if (!apifySnapshot.empty) {
          stats.apifyFetchLastRefresh = apifySnapshot.docs[0].data().lastUpdated;
        }

        // Check footfall analytics for this location
        const footfallQuery = query(
          collection(db, "users", uid, "fly_workspaces", locationId, "footfall_analytics"),
          orderBy("generated_at", "desc"),
          limit(1)
        );
        const footfallSnapshot = await getDocs(footfallQuery);
        if (!footfallSnapshot.empty) {
          stats.footfallInsightsLastRefresh = footfallSnapshot.docs[0].data().generated_at;
        }

      } catch (error) {
        console.warn(`Error calculating refresh stats for location ${locationId}:`, error);
      }

      return stats;
    },



    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      
      return `${day} ${month} ${year}`;
    },

    formatDateFull(timestamp) {
      if (!timestamp) return 'N/A';
      
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    },

    formatFootfallDate(dateString) {
      if (!dateString) return 'N/A';
      
      try {
        // Handle YYYY-MM-DD format
        const date = new Date(dateString);
        
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        
        return `${day} ${month} ${year}`;
      } catch (error) {
        console.warn('Error formatting footfall date:', dateString, error);
        return dateString; // Return original if formatting fails
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return 'N/A';
      
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${hours}:${minutes}`;
    },

    getCountryName(countryCode) {
      const countryMap = {
        'US': 'United States',
        'IN': 'India',
        'CA': 'Canada',
        'GB': 'United Kingdom',
        'AU': 'Australia',
        'DE': 'Germany',
        'FR': 'France',
        'JP': 'Japan',
        'BR': 'Brazil',
        'MX': 'Mexico',
        'Unknown': 'Unknown'
      };
      return countryMap[countryCode] || countryCode;
    },

    async createBrandTree(parentWorkspaces, locations) {
      const brandTree = [];
      
      console.log(`🌳 Creating brand tree with ${parentWorkspaces.length} brands`);
      
      // Create brand entries without loading child workspaces initially
      for (const parent of parentWorkspaces) {
        console.log(`🏗️ Creating brand entry for: ${parent.brandName || parent.name}`);
        console.log(`   Brand ID: ${parent.id}`);
        console.log(`   Child Workspaces Count: ${parent.childCount}`);
        
        // Use childCount instead of loading actual children
        const totalLocations = parent.childCount;
        
        // Calculate brand metrics
        const brandMetrics = await this.calculateBrandMetrics(parent);
        
        // Create brand entry using brandName
        const brandEntry = {
          id: `brand_${parent.uid}_${parent.id}`,
          name: parent.brandName || parent.name, // Use brandName if available, fallback to name
          workspaceName: parent.name, // Keep original workspace name for reference
          type: 'Brand',
          isBrand: true,
          locationCount: totalLocations,
          gmbConnected: `0/${totalLocations}`, // Will be calculated when children are loaded
          country: parent.country,
          uid: parent.uid,
          parentWid: parent.id,
          children: [], // Will be loaded on-demand
          childrenLoaded: false, // Flag to track if children have been loaded
          // Brand-specific metrics
          recentPost: brandMetrics.recentPost,
          recentPostDaysAgo: brandMetrics.recentPostDaysAgo,
          scheduledPosts: brandMetrics.scheduledPosts,
          scheduledPostsDetails: brandMetrics.scheduledPostsDetails,
          scheduledUpToDate: brandMetrics.scheduledUpToDate,
          footfallAnalytics: brandMetrics.footfallAnalytics,
          footfallAnalyticsComplete: brandMetrics.footfallAnalyticsComplete,
          footfallAnalyticsDefault: brandMetrics.footfallAnalyticsDefault,
          reviewDashboard: brandMetrics.reviewDashboard,
          keywordInsightsSummary: brandMetrics.keywordInsightsSummary,
          profileCompletion: brandMetrics.profileCompletion,
          overallRank: brandMetrics.overallRank,
          // Aggregated refresh stats (will be calculated from child stores when loaded)
          refreshStats: {
            rankLastRefresh: null,
            reviewInsightsLastRefresh: null,
            keywordInsightsLastRefresh: null,
            competitorsLastRefresh: null,
            apifyFetchLastRefresh: null,
            footfallInsightsLastRefresh: null
          }
        };
        
        console.log(`🏁 Brand entry created for ${parent.brandName} with ${totalLocations} locations (children will be loaded on-demand)`);
        brandTree.push(brandEntry);
      }
      
      console.log(`🌳 Brand tree creation completed with ${brandTree.length} brand entries`);
      return brandTree;
    },

    aggregateRefreshStats(locations) {
      // Aggregate refresh stats for brand level
      const stats = {
        rankLastRefresh: null,
        reviewInsightsLastRefresh: null,
        keywordInsightsLastRefresh: null,
        competitorsLastRefresh: null,
        apifyFetchLastRefresh: null,
        footfallInsightsLastRefresh: null
      };
      
      // Find the most recent refresh date for each type across all locations
      Object.keys(stats).forEach(statKey => {
        const dates = locations
          .map(loc => loc.refreshStats[statKey])
          .filter(date => date)
          .sort((a, b) => new Date(b) - new Date(a));
        
        if (dates.length > 0) {
          stats[statKey] = dates[0]; // Most recent date
        }
      });
      
      return stats;
    },

    async calculateBrandMetrics(brandWorkspace) {
      const metrics = {
        recentPost: null,
        recentPostDaysAgo: null,
        scheduledPosts: 0,
        scheduledPostsDetails: [],
        scheduledUpToDate: null,
        footfallAnalytics: null,
        footfallAnalyticsComplete: false,
        footfallAnalyticsDefault: null,
        reviewDashboard: null,
        keywordInsightsSummary: null,
        profileCompletion: 0,
        overallRank: null
      };

      try {
        console.log(`📊 Calculating brand metrics for: ${brandWorkspace.brandName}`);
        
        // Query social posts from the brand workspace itself (parent workspace)
        // Try both the brand workspace ID and "default" workspace as fallback
        let postsRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", brandWorkspace.id, "social-posts");
        
        console.log(`🔍 Querying social posts for brand ${brandWorkspace.brandName} from workspace ${brandWorkspace.id}`);
        
        // Try to get most recent published post - first try with posted_time ordering
        let recentPostSnapshot;
        let foundInWorkspace = "";
        
        console.log(`🔍 Searching for recent published posts for brand ${brandWorkspace.brandName}...`);
        
        try {
          // Try brand workspace first with posted_time ordering
          let recentPostQuery = query(
          postsRef,
            where("post_status", "==", "published"),
            orderBy("posted_time", "desc"),
            limit(1)
          );
          recentPostSnapshot = await getDocs(recentPostQuery);
          foundInWorkspace = brandWorkspace.id;
          
          console.log(`📊 Brand workspace ${brandWorkspace.id}: ${recentPostSnapshot.size} posts with posted_time`);
          
          // If no posts found with posted_time, try with created_at
          if (recentPostSnapshot.empty) {
            console.log(`🔄 Trying brand workspace with created_at ordering...`);
            recentPostQuery = query(
              postsRef,
              where("post_status", "==", "published"),
          orderBy("created_at", "desc"),
          limit(1)
        );
            recentPostSnapshot = await getDocs(recentPostQuery);
            console.log(`📊 Brand workspace ${brandWorkspace.id}: ${recentPostSnapshot.size} posts with created_at`);
          }
          
        } catch (error) {
          console.warn(`⚠️ Error querying brand workspace ${brandWorkspace.id}:`, error);
          recentPostSnapshot = { empty: true, size: 0 };
        }
        
        // If no posts found in brand workspace, try default workspace
        if (recentPostSnapshot.empty) {
          console.log(`📝 No published posts in brand workspace ${brandWorkspace.id}, trying default workspace...`);
          const defaultPostsRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", "default", "social-posts");
          
          try {
            // Try default workspace with posted_time ordering
            let recentPostQuery = query(
              defaultPostsRef,
              where("post_status", "==", "published"),
              orderBy("posted_time", "desc"),
              limit(1)
            );
            recentPostSnapshot = await getDocs(recentPostQuery);
            foundInWorkspace = "default";
            postsRef = defaultPostsRef; // Update reference for scheduled posts query
            
            console.log(`📊 Default workspace: ${recentPostSnapshot.size} posts with posted_time`);
            
            // If no posts found with posted_time, try with created_at
            if (recentPostSnapshot.empty) {
              console.log(`🔄 Trying default workspace with created_at ordering...`);
              recentPostQuery = query(
                defaultPostsRef,
                where("post_status", "==", "published"),
                orderBy("created_at", "desc"),
                limit(1)
              );
              recentPostSnapshot = await getDocs(recentPostQuery);
              console.log(`📊 Default workspace: ${recentPostSnapshot.size} posts with created_at`);
            }
            
          } catch (error) {
            console.warn(`⚠️ Error querying default workspace:`, error);
            recentPostSnapshot = { empty: true, size: 0 };
          }
        }
        
        if (!recentPostSnapshot.empty) {
          const postData = recentPostSnapshot.docs[0].data();
          const postId = recentPostSnapshot.docs[0].id;
          
          // Use posted_time if available, otherwise use created_at
          metrics.recentPost = postData.posted_time || postData.created_at;
          
          console.log(`📅 Found recent post in ${foundInWorkspace} workspace:`, {
            postId: postId,
            posted_time: postData.posted_time,
            created_at: postData.created_at,
            post_status: postData.post_status,
            when_to_post: postData.when_to_post,
            platforms: postData.social_media,
            text: postData.text ? postData.text.substring(0, 50) + '...' : 'No text'
          });
          
          // Calculate days ago
          if (metrics.recentPost) {
            let postDate;
            if (metrics.recentPost.toDate) {
              // Firestore Timestamp
              postDate = metrics.recentPost.toDate();
            } else if (typeof metrics.recentPost === 'string') {
              // String date
              postDate = new Date(metrics.recentPost);
            } else {
              // Already a Date object
              postDate = metrics.recentPost;
            }
            
            const now = new Date();
            const diffTime = Math.abs(now - postDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            metrics.recentPostDaysAgo = diffDays;
            
            console.log(`✅ Recent post calculation:`, {
              postDate: postDate,
              now: now,
              diffDays: diffDays,
              displayText: `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
            });
          }
        } else {
          console.log(`❌ No published posts found for brand ${brandWorkspace.brandName} in any workspace`);
          metrics.recentPost = null;
          metrics.recentPostDaysAgo = null;
        }

        // Get all scheduled posts from the same workspace we found posts in
        const now = new Date();
        const scheduledPostsQuery = query(
          postsRef,
          where("when_to_post", "==", "schedule"),
          orderBy("scheduled_at", "asc")
        );
        const scheduledPostsSnapshot = await getDocs(scheduledPostsQuery);
        console.log(`📅 Found ${scheduledPostsSnapshot.size} total scheduled posts for brand ${brandWorkspace.brandName}`);
        
        // Filter future posts and collect details
        const futureScheduledPosts = [];
        const scheduledPostsByDate = new Map();
        let latestScheduledDate = null;
        
        scheduledPostsSnapshot.docs.forEach((doc, index) => {
          const postData = doc.data();
          const scheduledTime = postData.scheduled_at;
          
          console.log(`📅 Scheduled post ${index + 1}:`, {
            id: doc.id,
            when_to_post: postData.when_to_post,
            post_status: postData.post_status,
            scheduled_at: scheduledTime,
            scheduledTimeDate: scheduledTime ? (scheduledTime.toDate ? scheduledTime.toDate() : new Date(scheduledTime)) : null,
            now: now,
            isFuture: scheduledTime ? (scheduledTime.toDate ? scheduledTime.toDate() > now : new Date(scheduledTime) > now) : false,
            social_media: postData.social_media,
            text: postData.text ? postData.text.substring(0, 30) + '...' : 'No text'
          });
          
          // Handle both Firestore timestamp and string date formats
          let scheduledDate;
          if (scheduledTime) {
            if (scheduledTime.toDate) {
              // Firestore Timestamp
              scheduledDate = scheduledTime.toDate();
            } else if (typeof scheduledTime === 'string') {
              // String date like "2025-08-17T06:30:00.000Z"
              scheduledDate = new Date(scheduledTime);
            } else {
              // Already a Date object
              scheduledDate = scheduledTime;
            }
          }
          
          if (scheduledDate && scheduledDate > now) {
            const postDetail = {
              scheduledTime: scheduledDate,
              platforms: postData.social_media || [],
              text: postData.text ? postData.text.substring(0, 50) + '...' : 'No content'
            };
            futureScheduledPosts.push(postDetail);
            
            // Group by date for breakdown
            const dateKey = this.formatDate(scheduledDate);
            if (!scheduledPostsByDate.has(dateKey)) {
              scheduledPostsByDate.set(dateKey, []);
            }
            scheduledPostsByDate.get(dateKey).push(postDetail);
            
            // Track latest scheduled date
            if (!latestScheduledDate || scheduledDate > latestScheduledDate) {
              latestScheduledDate = scheduledDate;
            }
            
            console.log(`✅ Added future scheduled post for ${dateKey}`);
          }
        });
        
        metrics.scheduledPosts = futureScheduledPosts.length;
        metrics.scheduledPostsDetails = Array.from(scheduledPostsByDate.entries()).map(([date, posts]) => ({
          date,
          posts,
          count: posts.length
        }));
        metrics.scheduledUpToDate = latestScheduledDate;
        
        console.log(`📊 Scheduled posts summary for ${brandWorkspace.brandName}:`, {
          totalScheduled: scheduledPostsSnapshot.size,
          futureScheduled: futureScheduledPosts.length,
          scheduledUpToDate: latestScheduledDate,
          dateBreakdown: metrics.scheduledPostsDetails
        });

        // Get footfall analytics from parent workspace
        try {
          const footfallRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", brandWorkspace.id, "footfall_analytics");
          const footfallQuery = query(footfallRef, orderBy("generated_at", "desc"), limit(1));
          const footfallSnapshot = await getDocs(footfallQuery);
          
          if (!footfallSnapshot.empty) {
            const footfallDoc = footfallSnapshot.docs[0];
            const footfallData = footfallDoc.data();
            const documentDate = footfallDoc.id; // Document ID is the date
            
            metrics.footfallAnalytics = {
              date: documentDate,
              generated_at: footfallData.generated_at,
              summary: footfallData.summary
            };
            
            // Check if document is complete according to data model
            const requiredFields = ['summary', 'hourlyTrends', 'storeWise', 'generated_at', 'uid', 'parentWid'];
            const hasAllRequiredFields = requiredFields.every(field => footfallData.hasOwnProperty(field));
            const hasSummaryFields = footfallData.summary && 
              footfallData.summary.hasOwnProperty('totalStores') &&
              footfallData.summary.hasOwnProperty('weekdayAverage') &&
              footfallData.summary.hasOwnProperty('weekendAverage');
            
            metrics.footfallAnalyticsComplete = hasAllRequiredFields && hasSummaryFields;
            
            console.log(`📊 Footfall analytics found for ${documentDate}:`, {
              complete: metrics.footfallAnalyticsComplete,
              totalStores: footfallData.summary?.totalStores,
              generated: footfallData.generated_at
            });
          }
        } catch (error) {
          console.warn(`Error fetching footfall analytics for ${brandWorkspace.brandName}:`, error);
        }

        // Get keyword insights summary from default workspace
        try {
          const keywordSummaryRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", "default", "search_insights_summary");
          const keywordSummaryQuery = query(keywordSummaryRef, orderBy("lastUpdated", "desc"), limit(1));
          const keywordSummarySnapshot = await getDocs(keywordSummaryQuery);
          
          if (!keywordSummarySnapshot.empty) {
            const summaryDoc = keywordSummarySnapshot.docs[0];
            const summaryData = summaryDoc.data();
            const summaryId = summaryDoc.id; // This should be in YYYY-MM format
            
            // Check if we need to refresh (if a Monday has passed in the current month)
            const isUpToDate = this.checkKeywordInsightsUpToDate(summaryId);
            
            metrics.keywordInsightsSummary = {
              monthYear: summaryId,
              lastUpdated: summaryData.lastUpdated,
              isUpToDate: isUpToDate
            };
            
            console.log(`📊 Keyword insights summary found for ${summaryId}:`, {
              monthYear: summaryId,
              lastUpdated: summaryData.lastUpdated,
              isUpToDate: isUpToDate
            });
          }
        } catch (error) {
          console.warn(`Error fetching keyword insights summary for ${brandWorkspace.brandName}:`, error);
        }

        // Get footfall analytics from default workspace
        try {
          const footfallDefaultRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", "default", "footfall_analytics");
          const footfallDefaultQuery = query(footfallDefaultRef, orderBy("generated_at", "desc"), limit(1));
          const footfallDefaultSnapshot = await getDocs(footfallDefaultQuery);
          
          if (!footfallDefaultSnapshot.empty) {
            const footfallDoc = footfallDefaultSnapshot.docs[0];
            const footfallData = footfallDoc.data();
            const documentDate = footfallDoc.id; // Document ID is the YYYY-MM-DD date
            
            // Check if it needs refresh (missed Monday)
            const isUpToDate = this.checkMondayRefresh(documentDate);
            
            metrics.footfallAnalyticsDefault = {
              date: documentDate,
              generated_at: footfallData.generated_at,
              isUpToDate: isUpToDate
            };
            
            console.log(`📊 Footfall analytics (default) found for ${documentDate}:`, {
              date: documentDate,
              generated_at: footfallData.generated_at,
              isUpToDate: isUpToDate
            });
          }
        } catch (error) {
          console.warn(`Error fetching footfall analytics (default) for ${brandWorkspace.brandName}:`, error);
        }

        // Get review dashboard data from default workspace
        try {
          const reviewDashboardRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", "default", "review_dashboard");
          const reviewDashboardQuery = query(reviewDashboardRef, orderBy("updated_at", "desc"), limit(1));
          const reviewDashboardSnapshot = await getDocs(reviewDashboardQuery);
          
          if (!reviewDashboardSnapshot.empty) {
            const reviewDoc = reviewDashboardSnapshot.docs[0];
            const reviewData = reviewDoc.data();
            const updatedAt = reviewData.updated_at;
            
            // Check if it needs refresh (missed Monday)
            const isUpToDate = this.checkMondayRefreshFromTimestamp(updatedAt);
            
            metrics.reviewDashboard = {
              updated_at: updatedAt,
              document_id: reviewDoc.id,
              isUpToDate: isUpToDate
            };
            
            console.log(`📊 Review dashboard found:`, {
              document_id: reviewDoc.id,
              updated_at: updatedAt,
              isUpToDate: isUpToDate
            });
          }
        } catch (error) {
          console.warn(`Error fetching review dashboard for ${brandWorkspace.brandName}:`, error);
        }

        // Use metrices collection for profile completion and other statistics from parent workspace
        try {
          const metricesRef = collection(db, "users", brandWorkspace.uid, "fly_workspaces", brandWorkspace.id, "metrices");
          const metricesSnapshot = await getDocs(metricesRef);
          
          if (!metricesSnapshot.empty) {
            // Get the most recent metrics document
            const latestMetrics = metricesSnapshot.docs[0].data();
            metrics.profileCompletion = latestMetrics.profileCompletion || 0;
            metrics.overallRank = latestMetrics.overallRank || "N/A";
          } else {
            // Fallback to manual calculation if no metrices collection
        const workspaceData = brandWorkspace.data;
        let completedFields = 0;
            const totalFields = 10;

        if (workspaceData.name) completedFields++;
        if (workspaceData.brandName) completedFields++;
        if (workspaceData.phone) completedFields++;
        if (workspaceData.email) completedFields++;
        if (workspaceData.country) completedFields++;
        if (workspaceData.city) completedFields++;
        if (workspaceData.zipcode) completedFields++;
        if (workspaceData.connected_apps && workspaceData.connected_apps.length > 0) completedFields++;
        if (workspaceData.gmbLocationId) completedFields++;
        if (workspaceData.storeLocation) completedFields++;

        metrics.profileCompletion = Math.round((completedFields / totalFields) * 100);
        metrics.overallRank = "N/A";
          }
        } catch (error) {
          console.warn(`Error fetching metrices for ${brandWorkspace.brandName}:`, error);
          // Fallback calculation
          metrics.profileCompletion = 0;
          metrics.overallRank = "N/A";
        }

        console.log(`✅ Brand metrics calculated:`, metrics);
        return metrics;

      } catch (error) {
        console.warn(`Error calculating brand metrics for ${brandWorkspace.brandName}:`, error);
        return metrics;
      }
    },

    getPlatformIcon(platform) {
      try {
        if (!platform || typeof platform !== 'string') {
          return 'mdi-share-variant';
        }
        
        const platformIcons = {
          'facebook': 'mdi-facebook',
          'instagram': 'mdi-instagram', 
          'twitter': 'mdi-twitter',
          'x': 'mdi-twitter',
          'linkedin': 'mdi-linkedin',
          'pinterest': 'mdi-pinterest',
          'youtube': 'mdi-youtube',
          'tiktok': 'mdi-music-note',
          'google_business_profile': 'mdi-google',
          'google': 'mdi-google',
          'threads': 'mdi-at'
        };
        return platformIcons[platform.toLowerCase()] || 'mdi-share-variant';
      } catch (error) {
        console.warn('Error in getPlatformIcon:', error, 'platform:', platform);
        return 'mdi-share-variant';
      }
    },

    getPlatformColor(platform) {
      try {
        if (!platform || typeof platform !== 'string') {
          return 'grey';
        }
        
        const platformColors = {
          'facebook': '#1877F2',
          'instagram': '#E4405F',
          'twitter': '#1DA1F2', 
          'x': '#000000',
          'linkedin': '#0A66C2',
          'pinterest': '#BD081C',
          'youtube': '#FF0000',
          'tiktok': '#000000',
          'google_business_profile': '#4285F4',
          'google': '#4285F4',
          'threads': '#000000'
        };
        return platformColors[platform.toLowerCase()] || 'grey';
      } catch (error) {
        console.warn('Error in getPlatformColor:', error, 'platform:', platform);
        return 'grey';
      }
    },

    getPlatformName(platform) {
      try {
        if (!platform || typeof platform !== 'string') {
          return 'Unknown Platform';
        }
        
        const platformNames = {
          'facebook': 'Facebook',
          'instagram': 'Instagram',
          'twitter': 'Twitter',
          'x': 'X (Twitter)',
          'linkedin': 'LinkedIn', 
          'pinterest': 'Pinterest',
          'youtube': 'YouTube',
          'tiktok': 'TikTok',
          'google_business_profile': 'Google Business Profile',
          'google': 'Google',
          'threads': 'Threads'
        };
        return platformNames[platform.toLowerCase()] || platform;
      } catch (error) {
        console.warn('Error in getPlatformName:', error, 'platform:', platform);
        return 'Unknown Platform';
      }
    },

    checkKeywordInsightsUpToDate(monthYearString) {
      try {
        // Parse the YYYY-MM format
        const [year, month] = monthYearString.split('-').map(Number);
        const summaryMonth = new Date(year, month - 1, 1);
        
        const now = new Date();
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        
        // If summary is from a previous month, it's definitely outdated
        if (summaryMonth < currentMonth) {
          return false;
        }
        
        // If summary is from current month, check if any Monday has passed this month
        if (summaryMonth.getTime() === currentMonth.getTime()) {
          const firstMondayThisMonth = this.getFirstMondayOfMonth(now.getFullYear(), now.getMonth());
          return now < firstMondayThisMonth;
        }
        
        // If summary is from future month, it's up to date
        return true;
      } catch (error) {
        console.warn('Error checking keyword insights date:', error);
        return false;
      }
    },

    getFirstMondayOfMonth(year, month) {
      const firstDay = new Date(year, month, 1);
      const dayOfWeek = firstDay.getDay();
      // Calculate days to add to get to first Monday (0 = Sunday, 1 = Monday)
      const daysToAdd = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7;
      return new Date(year, month, 1 + daysToAdd);
    },

    checkMondayRefresh(dateString) {
      try {
        // Parse YYYY-MM-DD format
        const documentDate = new Date(dateString);
        const now = new Date();
        
        // Find the most recent Monday
        const daysSinceMonday = (now.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0 system
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() - daysSinceMonday);
        lastMonday.setHours(0, 0, 0, 0);
        
        // If document is older than last Monday, it needs refresh
        return documentDate >= lastMonday;
      } catch (error) {
        console.warn('Error checking Monday refresh for date:', dateString, error);
        return false;
      }
    },

    checkMondayRefreshFromTimestamp(timestamp) {
      try {
        let updateDate;
        if (timestamp.toDate) {
          // Firestore Timestamp
          updateDate = timestamp.toDate();
        } else if (typeof timestamp === 'string') {
          // String format like "July 31, 2025 at 6:13:04 PM UTC+5:30"
          updateDate = new Date(timestamp);
        } else {
          // Already a Date object
          updateDate = timestamp;
        }
        
        const now = new Date();
        
        // Find the most recent Monday
        const daysSinceMonday = (now.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0 system
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() - daysSinceMonday);
        lastMonday.setHours(0, 0, 0, 0);
        
        // If update is older than last Monday, it needs refresh
        return updateDate >= lastMonday;
      } catch (error) {
        console.warn('Error checking Monday refresh from timestamp:', timestamp, error);
        return false;
      }
    },

    async onBrandExpanded(expandedData) {
      const { item, value } = expandedData;
      
      // Only load children when expanding (value = true) and if not already loaded
      if (value && item.isBrand && !item.childrenLoaded) {
        console.log(`🔄 Loading child workspaces for brand: ${item.name}`);
        
        try {
          const childWorkspaces = await this.loadChildWorkspaces(item.uid, item.parentWid);
          
          // Update the brand item with loaded children
          const brandIndex = this.brandWorkspacesTree.findIndex(brand => brand.id === item.id);
          if (brandIndex !== -1) {
            this.$set(this.brandWorkspacesTree[brandIndex], 'children', childWorkspaces);
            this.$set(this.brandWorkspacesTree[brandIndex], 'childrenLoaded', true);
            
            // Update GMB connected count
            const gmbConnected = childWorkspaces.filter(child => child.gmbConnected === 'Yes').length;
            this.$set(this.brandWorkspacesTree[brandIndex], 'gmbConnected', `${gmbConnected}/${childWorkspaces.length}`);
            
            console.log(`✅ Loaded ${childWorkspaces.length} child workspaces for ${item.name}`);
          }
        } catch (error) {
          console.error(`Error loading child workspaces for ${item.name}:`, error);
          this.$snackbar?.show(`Error loading locations for ${item.name}`);
        }
      }
    },

    async loadChildWorkspaces(uid, parentWid) {
      try {
        console.log(`🔍 Fetching child workspaces for user ${uid}, parent workspace ${parentWid}`);
        
        // Query all workspaces for this user
        const workspacesRef = collection(db, "users", uid, "fly_workspaces");
        const workspacesSnapshot = await getDocs(workspacesRef);
        
        const childWorkspaces = [];
        
        workspacesSnapshot.forEach(wsDoc => {
          const wsData = wsDoc.data();
          const wsId = wsDoc.id;
          
          // Check if this workspace has admin field matching the parent workspace ID
          if (wsData.admin === parentWid) {
            const childWorkspace = {
              id: `store_${uid}_${wsId}`,
              name: `└── ${wsData.name || 'Unnamed Location'}`,
              type: wsData.storeWorkspace ? 'Store' : 'Location',
              isBrand: false,
              locationCount: 1,
              gmbConnected: wsData.gmbLocationId ? 'Yes' : 'No',
              country: wsData.country_code || 'Unknown',
              uid: uid,
              parentWid: parentWid,
              workspaceId: wsId,
              // Basic store info - detailed metrics can be added later if needed
              profileCompletion: 0,
              refreshStats: {
                rankLastRefresh: null,
                reviewInsightsLastRefresh: null,
                keywordInsightsLastRefresh: null,
                competitorsLastRefresh: null,
                apifyFetchLastRefresh: null,
                footfallInsightsLastRefresh: null
              }
            };
            
            childWorkspaces.push(childWorkspace);
            console.log(`   ✅ Found child workspace: ${wsData.name} (${wsId})`);
          }
        });
        
        console.log(`🏁 Found ${childWorkspaces.length} child workspaces`);
        return childWorkspaces;
        
      } catch (error) {
        console.error('Error loading child workspaces:', error);
        throw error;
      }
    },



    clearFilters() {
      this.searchQuery = '';
      this.selectedCountry = null;
      console.log('🧹 Filters cleared');
    },

    clearUserFilters() {
      this.emailFilter = '';
      this.userIdFilter = '';
      console.log('🧹 User filters cleared');
    },

    async calculateStoreMetrics(storeWorkspace, parentUid) {
      const metrics = {
        rankRefresh: null,
        reviewInsights: null,
        keywordInsights: null,
        competitors: null,
        apifyFetch: null,
        footfallInsights: null,
        profileCompletion: 0
      };

      try {
        console.log(`📊 Calculating store metrics for: ${storeWorkspace.name}`);
        
        const uid = parentUid;
        const storeId = storeWorkspace.id;

        // Check rank tracker runs
        const rankTrackerQuery = query(
          collection(db, "users", uid, "rank_tracker_runs"),
          where("workspace_id", "==", storeId),
          orderBy("created_at", "desc"),
          limit(1)
        );
        const rankTrackerSnapshot = await getDocs(rankTrackerQuery);
        if (!rankTrackerSnapshot.empty) {
          metrics.rankRefresh = rankTrackerSnapshot.docs[0].data().created_at;
        }

        // Check review insights
        const reviewInsightsQuery = query(
          collection(db, "users", uid, "fly_workspaces", storeId, "review_insights"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const reviewInsightsSnapshot = await getDocs(reviewInsightsQuery);
        if (!reviewInsightsSnapshot.empty) {
          metrics.reviewInsights = reviewInsightsSnapshot.docs[0].data().lastUpdated;
        }

        // Check search insights (keyword insights)
        const searchInsightsQuery = query(
          collection(db, "users", uid, "fly_workspaces", storeId, "search_insights"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const searchInsightsSnapshot = await getDocs(searchInsightsQuery);
        if (!searchInsightsSnapshot.empty) {
          metrics.keywordInsights = searchInsightsSnapshot.docs[0].data().lastUpdated;
        }

        // Check competitors data
        const competitorsQuery = query(
          collection(db, "users", uid, "fly_workspaces", storeId, "competitors"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const competitorsSnapshot = await getDocs(competitorsQuery);
        if (!competitorsSnapshot.empty) {
          metrics.competitors = competitorsSnapshot.docs[0].data().lastUpdated;
        }

        // Check Apify fetch data
        const apifyQuery = query(
          collection(db, "users", uid, "fly_workspaces", storeId, "apify_data"),
          orderBy("lastUpdated", "desc"),
          limit(1)
        );
        const apifySnapshot = await getDocs(apifyQuery);
        if (!apifySnapshot.empty) {
          metrics.apifyFetch = apifySnapshot.docs[0].data().lastUpdated;
        }

        // Check footfall analytics
        const footfallQuery = query(
          collection(db, "users", uid, "fly_workspaces", storeId, "footfall_analytics"),
          orderBy("generated_at", "desc"),
          limit(1)
        );
        const footfallSnapshot = await getDocs(footfallQuery);
        if (!footfallSnapshot.empty) {
          metrics.footfallInsights = footfallSnapshot.docs[0].data().generated_at;
        }

        // Try to get profile completion from metrices collection first
        try {
          const metricesRef = collection(db, "users", uid, "fly_workspaces", storeId, "metrices");
          const metricesSnapshot = await getDocs(metricesRef);
          
          if (!metricesSnapshot.empty) {
            const latestMetrics = metricesSnapshot.docs[0].data();
            metrics.profileCompletion = latestMetrics.profileCompletion || 0;
          } else {
            // Fallback to manual calculation
        const storeData = storeWorkspace.data;
        let completedFields = 0;
        const totalFields = 8;

        if (storeData.name) completedFields++;
        if (storeData.phone) completedFields++;
        if (storeData.email) completedFields++;
        if (storeData.country) completedFields++;
        if (storeData.city) completedFields++;
        if (storeData.gmbLocationId) completedFields++;
        if (storeData.storeLocation) completedFields++;
        if (storeData.connected_apps && storeData.connected_apps.length > 0) completedFields++;

        metrics.profileCompletion = Math.round((completedFields / totalFields) * 100);
          }
        } catch (error) {
          console.warn(`Error fetching metrices for store ${storeWorkspace.name}:`, error);
          // Fallback calculation
          metrics.profileCompletion = 0;
        }

        console.log(`✅ Store metrics calculated for ${storeWorkspace.name}:`, metrics);
        return metrics;

      } catch (error) {
        console.warn(`Error calculating store metrics for ${storeWorkspace.name}:`, error);
        return metrics;
      }
    }
  }
};
</script>

<style scoped>
.operations-dashboard {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.dashboard-header {
  margin-bottom: 16px;
  text-align: center;
}

.v-card {
  border-radius: 12px;
}

.v-card--outlined {
  border: 2px solid #e0e0e0;
}

.v-expansion-panel-content {
  background-color: #fafafa;
}

@media (max-width: 960px) {
  .operations-dashboard {
    padding: 16px;
  }
}

.store-row {
  background-color: #f8f9fa !important;
}

.store-row:hover {
  background-color: #e9ecef !important;
}

.store-row td {
  border-bottom: 1px solid #dee2e6 !important;
  font-size: 0.875rem;
}

.store-header-row {
  background-color: #e3f2fd !important;
}

.store-header-row th {
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  color: #1976d2 !important;
  border-bottom: 2px solid #1976d2 !important;
  padding: 8px !important;
}

/* Timeline Styles */
.timeline-connector {
  width: 2px;
  height: 20px;
  background-color: #e0e0e0;
  margin-top: 2px;
}

.timeline-item {
  position: relative;
}

.timeline-content {
  padding-bottom: 8px;
}

.post-item {
  font-size: 0.75rem;
  margin-left: 8px;
}
</style>